import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardList, User, StickyNote, Bell, ArrowRightLeft, Mail,
  Mic, CheckCircle2, UserPlus, UserMinus, RefreshCw, ChevronLeft, ChevronRight,
  Search, Filter
} from 'lucide-react';
import api from '../../services/api';

interface ActivityLogEntry {
  id: number;
  user_id: number;
  user_name: string;
  entity_type: string;
  entity_id: number;
  entity_name: string;
  action: string;
  detail: string | null;
  old_value: string | null;
  new_value: string | null;
  created_at: string;
}

interface ActivityLogResponse {
  items: ActivityLogEntry[];
  total: number;
  page: number;
  size: number;
}

const ACTION_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  create: { icon: <UserPlus className="h-3.5 w-3.5" />, color: 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/30', label: 'Création' },
  update: { icon: <ArrowRightLeft className="h-3.5 w-3.5" />, color: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/30', label: 'Modification' },
  status_change: { icon: <ArrowRightLeft className="h-3.5 w-3.5" />, color: 'text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-900/30', label: 'Statut' },
  rdv_change: { icon: <ArrowRightLeft className="h-3.5 w-3.5" />, color: 'text-purple-600 bg-purple-50 dark:text-purple-400 dark:bg-purple-900/30', label: 'RDV' },
  cote_change: { icon: <ArrowRightLeft className="h-3.5 w-3.5" />, color: 'text-indigo-600 bg-indigo-50 dark:text-indigo-400 dark:bg-indigo-900/30', label: 'Cote' },
  email_change: { icon: <Mail className="h-3.5 w-3.5" />, color: 'text-cyan-600 bg-cyan-50 dark:text-cyan-400 dark:bg-cyan-900/30', label: 'Email' },
  note_added: { icon: <StickyNote className="h-3.5 w-3.5" />, color: 'text-yellow-600 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-900/30', label: 'Note' },
  voice_note_added: { icon: <Mic className="h-3.5 w-3.5" />, color: 'text-pink-600 bg-pink-50 dark:text-pink-400 dark:bg-pink-900/30', label: 'Vocale' },
  reminder_added: { icon: <Bell className="h-3.5 w-3.5" />, color: 'text-orange-600 bg-orange-50 dark:text-orange-400 dark:bg-orange-900/30', label: 'Rappel' },
  reminder_completed: { icon: <CheckCircle2 className="h-3.5 w-3.5" />, color: 'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/30', label: 'Rappel OK' },
  convert: { icon: <ArrowRightLeft className="h-3.5 w-3.5" />, color: 'text-green-700 bg-green-50 dark:text-green-300 dark:bg-green-900/30', label: 'Conversion' },
  assign: { icon: <UserPlus className="h-3.5 w-3.5" />, color: 'text-teal-600 bg-teal-50 dark:text-teal-400 dark:bg-teal-900/30', label: 'Affectation' },
  unassign: { icon: <UserMinus className="h-3.5 w-3.5" />, color: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/30', label: 'Désaffectation' },
};

const DEFAULT_ACTION = { icon: <ClipboardList className="h-3.5 w-3.5" />, color: 'text-slate-600 bg-slate-50 dark:text-slate-400 dark:bg-slate-800', label: 'Action' };

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMin / 60);
  const diffD = Math.floor(diffH / 24);

  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin}min`;
  if (diffH < 24) return `il y a ${diffH}h`;
  if (diffD < 7) return `il y a ${diffD}j`;
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

const ACTION_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Toutes actions' },
  { value: 'create', label: 'Création' },
  { value: 'update', label: 'Modification' },
  { value: 'status_change', label: 'Statut' },
  { value: 'rdv_change', label: 'RDV' },
  { value: 'cote_change', label: 'Cote' },
  { value: 'email_change', label: 'Email' },
  { value: 'note_added', label: 'Note' },
  { value: 'voice_note_added', label: 'Vocale' },
  { value: 'reminder_added', label: 'Rappel' },
  { value: 'reminder_completed', label: 'Rappel OK' },
  { value: 'convert', label: 'Conversion' },
  { value: 'assign', label: 'Affectation' },
  { value: 'unassign', label: 'Désaffectation' },
];

const ActivityLogPanel: React.FC = () => {
  const [data, setData] = useState<ActivityLogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [entityFilter, setEntityFilter] = useState<string>('');
  const [actionFilter, setActionFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const pageSize = 15;

  const hasActiveFilters = !!entityFilter || !!actionFilter || !!search;

  const fetchLog = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = { page, size: pageSize };
      if (search) params.search = search;
      if (entityFilter) params.entity_type = entityFilter;
      if (actionFilter) params.action = actionFilter;
      const res = await api.get('/activity-log', { params });
      setData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [page, search, entityFilter, actionFilter]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-6 border-2 border-slate-200 dark:border-slate-700 shadow-sm">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h3 className="text-sm sm:text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <ClipboardList className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
          Journal d'activité
          {data && <span className="text-xs font-normal text-slate-400 ml-1">({data.total})</span>}
        </h3>
        <div className="flex items-center gap-1.5">
          {/* Toggle filters */}
          <button
            onClick={() => setShowFilters(f => !f)}
            className={`p-1.5 rounded-lg border transition-colors ${hasActiveFilters
              ? 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/30'
              : 'border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800'
              }`}
            title="Filtres"
          >
            <Filter className={`h-3.5 w-3.5 ${hasActiveFilters ? 'text-blue-600' : 'text-slate-500'}`} />
          </button>
          {/* Refresh */}
          <button
            onClick={fetchLog}
            disabled={loading}
            className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            title="Rafraîchir"
          >
            <RefreshCw className={`h-3.5 w-3.5 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filters row */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-slate-100 dark:border-slate-800">
          {/* Entity filter */}
          <select
            value={entityFilter}
            onChange={(e) => { setEntityFilter(e.target.value); setPage(1); }}
            className="text-[11px] border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-pointer"
          >
            <option value="">Tous types</option>
            <option value="prospect">Prospects</option>
            <option value="client">Clients</option>
          </select>
          {/* Action filter */}
          <select
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
            className="text-[11px] border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-pointer"
          >
            {ACTION_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {/* Search */}
          <form onSubmit={handleSearch} className="flex">
            <div className="relative">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Nom, détail, utilisateur..."
                className="text-[11px] border border-slate-200 dark:border-slate-600 rounded-lg pl-6 pr-2 py-1 w-40 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 placeholder-slate-400"
              />
              <Search className="h-3 w-3 text-slate-400 absolute left-1.5 top-1/2 -translate-y-1/2" />
            </div>
          </form>
          {/* Clear filters */}
          {hasActiveFilters && (
            <button
              onClick={() => { setEntityFilter(''); setActionFilter(''); setSearch(''); setSearchInput(''); setPage(1); }}
              className="text-[10px] text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium px-1.5 py-0.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              Effacer
            </button>
          )}
        </div>
      )}

      {/* Log entries */}
      <div className="space-y-0 divide-y divide-slate-100 dark:divide-slate-800 max-h-[480px] overflow-y-auto">
        {loading && !data ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        ) : data && data.items.length > 0 ? (
          data.items.map((entry) => {
            const config = ACTION_CONFIG[entry.action] || DEFAULT_ACTION;
            return (
              <div key={entry.id} className="flex items-start gap-3 py-2.5 px-1 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors rounded">
                {/* Icon */}
                <div className={`flex-shrink-0 p-1.5 rounded-lg ${config.color}`}>
                  {config.icon}
                </div>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded ${config.color}`}>
                      {config.label}
                    </span>
                    <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 capitalize">
                      {entry.entity_type}
                    </span>
                    <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate max-w-[140px]" title={entry.entity_name}>
                      {entry.entity_name}
                    </span>
                  </div>
                  {entry.detail && (
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">
                      {entry.detail}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500">
                      <User className="h-2.5 w-2.5" />
                      {entry.user_name}
                    </span>
                    <span className="text-[10px] text-slate-300 dark:text-slate-600">•</span>
                    <span className="text-[10px] text-slate-400 dark:text-slate-500" title={new Date(entry.created_at).toLocaleString('fr-FR')}>
                      {formatRelativeTime(entry.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8 text-sm text-slate-400">
            Aucune activité enregistrée
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-3 mt-3 border-t border-slate-100 dark:border-slate-800">
          <span className="text-[10px] text-slate-400">
            Page {page}/{totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 transition-colors"
            >
              <ChevronLeft className="h-4 w-4 text-slate-500" />
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 transition-colors"
            >
              <ChevronRight className="h-4 w-4 text-slate-500" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityLogPanel;
